import { PerformanceMetric, usePerformance } from '@/context/performance';
import { useBackgroundAppBundle } from '@/context/background-app-bundle';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppBundle } from '../../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';

interface AppBundleLoaderProps {
  appBundle: AppBundle;
  className?: string;
  renderApp: (appWrapper: HTMLDivElement) => Promise<() => void>;
  style?: string;
}

export function AppBundleLoader({ appBundle, className = '', renderApp, style }: AppBundleLoaderProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [loaded, setIsLoaded] = useState(false);
  const recordPerformanceMetric = usePerformance();
  const { getData: getBackgroundAppData, setData: setBackgroundAppData } = useBackgroundAppBundle(appBundle);

  useEffect(() => {
    setChildAppLoaded(false);
  }, []);

  function hideAppWrapper() {
    const appData = getBackgroundAppData();
    const appWrapper = appData?.appWrapper;
    if (appWrapper != null) {
      appWrapper.style.display = 'none';
    }
  }

  function showAppWrapper() {
    const appData = getBackgroundAppData();
    const appWrapper = appData?.appWrapper;
    if (appWrapper != null) {
      appWrapper.style.display = 'flex';
    }
  }

  const load = useCallback(async () => {
    recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);

    const appWrapperTemplate = document.createElement('template');
    appWrapperTemplate.innerHTML = `<div class="${className}" style="display: none; min-width: 0; flex: 1; ${style}"></div>`;

    const appWrapper = appWrapperTemplate.content.cloneNode(true).firstChild as HTMLDivElement;

    setBackgroundAppData({
      initialized: true,
      loaded: false,
      appWrapper,
    });

    if (ref.current != null) {
      ref.current.parentElement?.appendChild(appWrapper);
    }

    const cleanup = await renderApp(appWrapper);

    setBackgroundAppData({
      loaded: true,
      cleanup,
    });

    // TODO: See if we can remove any of these states
    setChildAppLoaded(true);
    setIsLoaded(true);
    showAppWrapper();
  }, [className, renderApp, recordPerformanceMetric, setBackgroundAppData]);

  useEffect(() => {
    const appData = getBackgroundAppData();

    if (appData?.initialized !== true || !document.body.contains(appData.appWrapper)) {
      load();
    } else {
      if (appData.loaded !== true) {
        return;
      }

      // TODO: See if we can remove any of these states
      setChildAppLoaded(true);
      setIsLoaded(true);
      showAppWrapper();
    }

    return () => {
      hideAppWrapper();
    };
  }, [className, renderApp, getBackgroundAppData]);

  return (
    <>
      <div ref={ref} style={{ display: 'none' }}></div>
      {loaded ? null : <PageLoader />}
    </>
  );
}
